// The flat-row editing surface for a section's RAW CSS StyleProps dictionaries.
//
// This is a deliberate twin of the Storefront Styles panel's PartPropertyRows, not a reuse of it.
// The chrome is shared (styleRows.tsx), the VALUES are not:
//
//   Styles panel parts (ISearchElementStyle) | section StyleProps (raw CSS)
//   ----------------------------------------|--------------------------------------------
//   `font`            -> fontFamily          | `fontFamily` directly
//   `fontSize: 120`   -> "120%"              | `fontSize: '1.2em'` — a CSS string, verbatim
//   `padding: 12`     -> 12px                | `padding: '12px 8px'` — a CSS string, verbatim
//
// Section StyleProps are spread straight into a React `style` prop by the shopper and the editor
// preview, so every value here must stay a valid CSS string. Running PartPropertyRows over them
// would write numbers where strings are expected and rename fontFamily to font — silent corruption.
//
// The property list mirrors Sections/CssForm (the layout this replaces): backgroundColor, color,
// padding, fontFamily, fontStyle, fontWeight, fontSize, textAlign, borderStyle, borderWidth,
// borderColor, borderRadius — plus boxShadow, which CssForm never exposed. Anything else a merchant
// has set stays reachable through the section's Advanced tab (Raw JSON over the whole section) and
// is never touched by these rows.

import React from 'react';
import { Dropdown, IDropdownOption, Stack, TextField } from '@fluentui/react';
import { Css } from 'nano-css/addon/vcssom/cssToTree';
import FontPicker from '../../../Inputs/FontPicker';
import { ColorRow, RowGroupLabel, StyleRow, TextAlignRow, useCellControlStyles } from '../styleRows';
import { DynamicSetting } from '../../../Common/Setting';

// A length we can round-trip as "number + unit". Everything else (calc(), multi-value shorthand,
// keywords like `thin` / `unset` / `larger`) is preserved verbatim in a plain text field.
const SIMPLE_LENGTH = /^\s*(-?\d*\.?\d+)\s*(px|%|em|rem|vh|vw|pt)?\s*$/i;

/**
 * A length row that never destroys what it can't model.
 *
 *  * `'12px'` / `'1.2em'` / `'50%'` → numeric field showing `12`, with the STORED unit as the
 *    suffix, so editing an em value keeps it in em.
 *  * empty → numeric field, blank; typing a bare number writes `{n}{defaultUnit}`.
 *  * typing a full value (`50%`, `calc(1em + 2px)`) is stored verbatim — the field is text, not
 *    number, precisely so units can be typed.
 *  * anything already stored that doesn't parse → shown raw in the same field, unsuffixed, and
 *    round-trips untouched unless the merchant edits it.
 */
const CssLengthRow: React.FC<{
    label: string;
    value?: Css[string];
    defaultUnit?: string;
    onChange: (value: string) => void;
}> = ({ label, value, defaultUnit = 'px', onChange }) => {
    const cell = useCellControlStyles();
    const raw = value === undefined || value === null ? '' : String(value);
    const match = raw === '' ? null : raw.match(SIMPLE_LENGTH);
    const simple = raw === '' || !!match;

    const commit = (next?: string) => {
        const s = (next ?? '').trim();
        if (!s) return onChange('');
        const m = s.match(SIMPLE_LENGTH);
        // A bare number picks up the row's default unit; anything else is stored as typed.
        onChange(m && !m[2] ? `${m[1]}${defaultUnit}` : s);
    };

    return (
        <StyleRow label={label}>
            <TextField
                value={simple ? (match?.[1] ?? '') : raw}
                suffix={simple ? (match?.[2] || defaultUnit) : undefined}
                title={simple ? undefined : `Raw CSS value: ${raw}`}
                onChange={(_, v) => commit(v)}
                styles={cell.textField}
            />
        </StyleRow>
    );
};

// `padding` is one CSS key holding up to four values. CssForm only ever wrote the "{V}px {H}px"
// shape, so that's what gets the friendly two-row treatment; a 3/4-value shorthand or any
// non-px unit falls back to a single verbatim field rather than being silently rewritten.
const PADDING_TWO = /^\s*(\d*\.?\d+)px\s+(\d*\.?\d+)px\s*$/i;
const PADDING_ONE = /^\s*(\d*\.?\d+)px\s*$/i;

const PaddingRows: React.FC<{ value?: Css[string]; onChange: (value: string) => void }> = ({ value, onChange }) => {
    const cell = useCellControlStyles();
    const raw = value === undefined || value === null ? '' : String(value);
    const two = raw.match(PADDING_TWO);
    const one = raw.match(PADDING_ONE);
    const simple = raw === '' || !!two || !!one;

    if (!simple) {
        return (
            <StyleRow label='Padding'>
                <TextField value={raw} title={`Raw CSS value: ${raw}`} onChange={(_, v) => onChange(v ?? '')} styles={cell.textField} />
            </StyleRow>
        );
    }

    const v = two?.[1] ?? one?.[1] ?? '';
    const h = two?.[2] ?? one?.[1] ?? '';
    const write = (nextV: string, nextH: string) =>
        onChange(!nextV && !nextH ? '' : `${nextV || '0'}px ${nextH || '0'}px`);

    return (
        <>
            <StyleRow label='Padding (vertical)'>
                <TextField type='number' min={0} suffix='px' value={v} onChange={(_, n) => write(n ?? '', h)} styles={cell.textField} />
            </StyleRow>
            <StyleRow label='Padding (horizontal)'>
                <TextField type='number' min={0} suffix='px' value={h} onChange={(_, n) => write(v, n ?? '')} styles={cell.textField} />
            </StyleRow>
        </>
    );
};

// Option lists carried over from CssForm verbatim (plus a leading "Default" that writes '' to
// unset), so every value a merchant has already saved still round-trips and displays.
const DEFAULT_OPTION: IDropdownOption = { key: '', text: 'Default' };

const FONT_STYLE_OPTIONS: IDropdownOption[] = [
    DEFAULT_OPTION,
    { key: 'normal', text: 'Normal' },
    { key: 'italic', text: 'Italic' },
    { key: 'oblique', text: 'Oblique' },
];

const FONT_WEIGHT_OPTIONS: IDropdownOption[] = [
    DEFAULT_OPTION,
    { key: 'unset', text: 'Unset' },
    { key: 'normal', text: 'Normal' },
    { key: 'lighter', text: 'Lighter' },
    { key: 'bold', text: 'Bold' },
    { key: 'bolder', text: 'Bolder' },
];

const BORDER_STYLE_OPTIONS: IDropdownOption[] = [
    DEFAULT_OPTION,
    { key: 'none', text: 'None' },
    { key: 'hidden', text: 'Hidden' },
    { key: 'dotted', text: 'Dotted' },
    { key: 'dashed', text: 'Dashed' },
    { key: 'solid', text: 'Solid' },
    { key: 'double', text: 'Double' },
    { key: 'groove', text: 'Groove' },
    { key: 'ridge', text: 'Ridge' },
    { key: 'inset', text: 'Inset' },
    { key: 'outset', text: 'Outset' },
    { key: 'unset', text: 'Unset' },
];

const str = (v?: Css[string]): string => (v === undefined || v === null ? '' : String(v));

interface ISectionCssRowsProps {
    css?: Css;
    /** Same contract as CssForm: one key at a time, '' to unset. */
    onChange: (key: string, value: any) => void;
    /**
     * Names whose style these rows edit, e.g. `storefront.sectionStyle.<id>.section`. This renderer
     * only sees a CSS bag and a change handler, so without it there is no id to build and the rows
     * stay unlinkable — the same contract ElementPropertyRows uses.
     */
    settingKeyPrefix?: string;
}

const SectionCssRows: React.FC<ISectionCssRowsProps> = ({ css, onChange, settingKeyPrefix }) => {
    const cell = useCellControlStyles();
    const mark = (property: string, control: React.ReactElement) =>
        settingKeyPrefix
            ? <DynamicSetting key={property} id={`${settingKeyPrefix}.${property}`}>{control}</DynamicSetting>
            : control;
    return (
        // 4px between rows — looser than the Styles panel's 2px, which felt cramped once the
        // section tab stacked four of these groups.
        <Stack tokens={{ childrenGap: 4 }}>
            {mark('backgroundColor', (
                <ColorRow label='Background' value={str(css?.backgroundColor)} onChange={(v) => onChange('backgroundColor', v ?? '')} />
            ))}
            {mark('color', (
                <ColorRow label='Text color' value={str(css?.color)} onChange={(v) => onChange('color', v ?? '')} />
            ))}
            <PaddingRows value={css?.padding} onChange={(v) => onChange('padding', v)} />

            <RowGroupLabel text='Text' iconName='Font' />
            {mark('fontFamily', (
                <StyleRow label='Font'>
                    <FontPicker selectedFont={str(css?.fontFamily)} placeholder='Default' onChange={(f) => onChange('fontFamily', f)} styles={cell.dropdown} />
                </StyleRow>
            ))}
            {mark('fontSize', (
                <CssLengthRow label='Font size' value={css?.fontSize} onChange={(v) => onChange('fontSize', v)} />
            ))}
            {mark('fontWeight', (
                <StyleRow label='Font weight'>
                    <Dropdown options={FONT_WEIGHT_OPTIONS} selectedKey={str(css?.fontWeight)} onChange={(_, o) => onChange('fontWeight', (o?.key as string) ?? '')} styles={cell.dropdown} />
                </StyleRow>
            ))}
            {mark('fontStyle', (
                <StyleRow label='Font style'>
                    <Dropdown options={FONT_STYLE_OPTIONS} selectedKey={str(css?.fontStyle)} onChange={(_, o) => onChange('fontStyle', (o?.key as string) ?? '')} styles={cell.dropdown} />
                </StyleRow>
            ))}
            {/* textAlign is one of the few keys both surfaces spell identically, so the shared
                button group drops straight in. */}
            <TextAlignRow value={str(css?.textAlign) || undefined} onChange={(v) => onChange('textAlign', v ?? '')} />

            <RowGroupLabel text='Border' iconName='BorderDot' />
            {mark('borderColor', (
                <ColorRow label='Border color' value={str(css?.borderColor)} onChange={(v) => onChange('borderColor', v ?? '')} />
            ))}
            {mark('borderWidth', (
                <CssLengthRow label='Border width' value={css?.borderWidth} onChange={(v) => onChange('borderWidth', v)} />
            ))}
            {mark('borderStyle', (
                <StyleRow label='Border style'>
                    <Dropdown options={BORDER_STYLE_OPTIONS} selectedKey={str(css?.borderStyle)} onChange={(_, o) => onChange('borderStyle', (o?.key as string) ?? '')} styles={cell.dropdown} />
                </StyleRow>
            ))}
            {mark('borderRadius', (
                <CssLengthRow label='Corner radius' value={css?.borderRadius} onChange={(v) => onChange('borderRadius', v)} />
            ))}
            {mark('boxShadow', (
                <StyleRow label='Box shadow'>
                    <TextField placeholder='0 2px 8px rgba(0,0,0,.2)' value={str(css?.boxShadow)} onChange={(_, v) => onChange('boxShadow', v ?? '')} styles={cell.textField} />
                </StyleRow>
            ))}
        </Stack>
    );
};

export default SectionCssRows;
